import { html, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../BaseElement";
import { LightTheme } from "../ThemeLight";

import { styles } from "./ToolDrawer.style";
import { litIf } from "../LitUtils";
import { choose } from "lit/directives/choose.js";

import "../FilterPicker/FilterPicker";
import { LocalStorageProperty } from "../../LocalStorageProperty";
import { IPlaceListTable } from "../../backend/IPlaceListTable";
import { makeDefaultPlaceFilter } from "../../backend/cloud/PlaceFilterTable";

type Tools = "filter" | "list";

@customElement("ds-tool-drawer")
export class ToolDrawer extends BaseElement {
    static override styles = [styles, LightTheme];

    @property({ type: Boolean }) open = false;
    @property({ type: String }) tool: Tools = "filter";

    @state() protected _list = new LocalStorageProperty(this._kernel.events, "tool-drawer-list", 1);

    private _placeListTable = this._kernel.dataStore.getSource<IPlaceListTable>("place-list");

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();

        // default to open on desktop
        if (window.innerWidth > 1024) {
            this._open();
        }

        // automatically close on mobile when a place is selected
        this.addEventListener("ds-view-on-map", () => {
            if (window.innerWidth < 1024) {
                this._close();
            }
        });
    }

    private _setTool(tool: Tools) {
        if (this.tool === tool && this.open) {
            this._close();
        } else {
            this.tool = tool;
            this._raiseEvent("ds-tool-changed", { tool });
            this._open();

            if (tool === "list") {
                this._filterByList(this._list.get());
            } else if (tool === "filter") {
                this._kernel.currentPlaceFilter.set(this._kernel.currentPlaceFilter.get());
            }
        }
    }

    private _close() {
        this.open = false;
        this._raiseEvent("ds-tool-closed", {});
    }

    private _open() {
        this.open = true;
        this._raiseEvent("ds-tool-opened", {});
    }

    private _renderFilter() {
        return html`<ds-filter-editor hideCloseButton autoShow></ds-filter-editor>`;
    }

    private _renderList() {
        return html`<ds-place-list-editor
            .placeList=${this._list.get()}
            @ds-list-changed=${async (e: CustomEvent) => {
                await this._filterByList(e.detail.list);
                this._list.set(e.detail.list);
            }}
        ></ds-place-list-editor>`;
    }

    private async _filterByList(listId: string) {
        const entries = await this._placeListTable.getEntries(listId);
        const filter = makeDefaultPlaceFilter();
        filter.filter.list = entries.map((x) => x.place);
        this._kernel.currentPlaceFilter.set(filter);
    }

    private _renderOpen() {
        return choose(this.tool, [
            ["filter", () => this._renderFilter()],
            ["list", () => this._renderList()],
        ]) as TemplateResult;
    }

    private _renderHeader() {
        return html`<div class="flex-row header" slot="header">
            <sl-button-group>
                <sl-button pill size="medium" @click=${() => this._setTool("filter")}
                    >Filters</sl-button
                >
                <sl-button pill size="medium" @click=${() => this._setTool("list")}
                    >Lists</sl-button
                >
            </sl-button-group>

            ${litIf(
                this.open,
                () =>
                    html`<sl-icon-button
                        name="x-lg"
                        @click=${() => this._close()}
                    ></sl-icon-button>`
            )}
        </div>`;
    }
    override render() {
        return html`<ds-fixed-panel noFooter>
            ${this._renderHeader()}
            ${litIf(
                this.open,
                () =>
                    html`<div slot="body" class="body ${this.open ? "open" : "closed"}">
                        ${this._renderOpen()}
                    </div>`
            )}
        </ds-fixed-panel>`;
    }
}
